// apps/web/src/auth/FetchInterceptor.js

import axios from 'axios'
import { API_BASE_URL } from 'configs/AppConfig'
import { signOutSuccess } from 'store/slices/authSlice'
import store from '../store'
import { AUTH_TOKEN } from 'constants/AuthConstant'
import { notification } from 'antd'

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
})

const clearAuthData = () => {
  localStorage.removeItem(AUTH_TOKEN)
  localStorage.removeItem('AUTH_TOKEN')
  localStorage.removeItem('ACCESS_TOKEN')
  localStorage.removeItem('AUTH_USER')
  localStorage.removeItem('USER_ROLE')
  localStorage.removeItem('CLIENT_ID')
  localStorage.removeItem('CLIENTS')
  localStorage.removeItem('USER_PERMISSIONS')
  localStorage.removeItem('CLIENT_USER_ROLE')
}

const normalizeApiMessage = (
  apiMessage,
  fallback,
) => {
  if (Array.isArray(apiMessage)) {
    return apiMessage.join(', ')
  }

  return apiMessage || fallback
}

/*
 * ============================================================
 * REQUEST INTERCEPTOR
 * ============================================================
 */

service.interceptors.request.use(
  (config) => {
    const jwtToken =
      localStorage.getItem(AUTH_TOKEN) ||
      localStorage.getItem('AUTH_TOKEN') ||
      localStorage.getItem('ACCESS_TOKEN')

    if (jwtToken) {
      config.headers.Authorization =
        `Bearer ${jwtToken}`
    }

    const clientId =
      localStorage.getItem('CLIENT_ID')

    if (clientId) {
      config.headers['x-client-id'] =
        clientId
    }

    return config
  },

  (error) => {
    /*
     * Se a própria configuração da
     * requisição pedir silêncio, não
     * mostramos notificação.
     */
    const silentNetworkError =
      error?.config
        ?.silentNetworkError === true

    if (!silentNetworkError) {
      notification.error({
        message: 'Erro',

        description:
          'Erro ao preparar requisição.',
      })
    }

    return Promise.reject(error)
  },
)

/*
 * ============================================================
 * RESPONSE INTERCEPTOR
 * ============================================================
 */

service.interceptors.response.use(
  (response) => response.data,

  (error) => {
    const status =
      error.response?.status

    const apiMessage =
      error.response?.data?.message

    /*
     * --------------------------------------------------------
     * MODO HÍBRIDO / OFFLINE
     * --------------------------------------------------------
     *
     * Algumas requisições da página pública
     * possuem fallback para IndexedDB.
     *
     * Exemplo:
     *
     * API
     *  ↓ falhou
     * IndexedDB
     *  ↓
     * continua funcionando
     *
     * Nesses casos a falha da API não deve
     * gerar uma notificação vermelha.
     */
    const silentNetworkError =
      error?.config
        ?.silentNetworkError === true

    /*
     * --------------------------------------------------------
     * ERRO DE REDE
     * --------------------------------------------------------
     *
     * Quando não existe status HTTP,
     * normalmente significa:
     *
     * - computador offline;
     * - API indisponível;
     * - timeout;
     * - falha de DNS;
     * - conexão interrompida.
     */
    if (!status) {
      console.error(
        '[NETWORK ERROR DEBUG]',
        {
          url:
            error?.config?.url,

          method:
            error?.config?.method,

          baseURL:
            error?.config?.baseURL,

          silentNetworkError:
            error?.config
              ?.silentNetworkError,

          navigatorOnline:
            navigator.onLine,

          message:
            error?.message,
        },
      )

      if (!silentNetworkError) {
        notification.error({
          message:
            'Erro de conexão',

          description:
            'Não foi possível conectar à API.',
        })
      }

      return Promise.reject(error)
    }

    /*
     * --------------------------------------------------------
     * ERROS HTTP
     * --------------------------------------------------------
     */

    let notificationParam = {
      message: 'Erro',

      description:
        normalizeApiMessage(
          apiMessage,
          'Ocorreu um erro na requisição.',
        ),
    }

    if (status === 400) {
      notificationParam = {
        message:
          'Dados inválidos',

        description:
          normalizeApiMessage(
            apiMessage,
            'Verifique os dados informados.',
          ),
      }
    }

    if (status === 401) {
      notificationParam = {
        message:
          'Sessão expirada',

        description:
          normalizeApiMessage(
            apiMessage,
            'Faça login novamente.',
          ),
      }

      clearAuthData()

      store.dispatch(
        signOutSuccess(),
      )
    }

    if (status === 403) {
      notificationParam = {
        message:
          'Acesso negado',

        description:
          normalizeApiMessage(
            apiMessage,
            'Você não possui permissão para realizar esta ação.',
          ),
      }
    }

    if (status === 404) {
      notificationParam = {
        message:
          'Não encontrado',

        description:
          normalizeApiMessage(
            apiMessage,
            'Recurso não encontrado.',
          ),
      }
    }

    if (status === 409) {
      notificationParam = {
        message:
          'Conflito',

        description:
          normalizeApiMessage(
            apiMessage,
            'Já existe um registro com estes dados.',
          ),
      }
    }

    if (status === 422) {
      notificationParam = {
        message:
          'Dados inválidos',

        description:
          normalizeApiMessage(
            apiMessage,
            'Não foi possível processar os dados informados.',
          ),
      }
    }

    if (status === 500) {
      notificationParam = {
        message:
          'Erro interno',

        description:
          normalizeApiMessage(
            apiMessage,
            'Erro interno do servidor.',
          ),
      }
    }

    if (status === 508) {
      notificationParam = {
        message:
          'Tempo esgotado',

        description:
          normalizeApiMessage(
            apiMessage,
            'A requisição demorou demais.',
          ),
      }
    }

    /*
     * silentNetworkError é destinado
     * principalmente a erros de conexão.
     *
     * Se o servidor respondeu com 400,
     * 401, 403, 500 etc., continuamos
     * mostrando o erro normalmente.
     */
    notification.error(
      notificationParam,
    )

    return Promise.reject(error)
  },
)

export default service